import React from 'react';
import { DownloadIcon, CheckCircleIcon, EuroIcon, FileTextIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
import { Button } from '../components/Button';
interface KostenPageProps {
  onNavigate: (page: string) => void;
}
export function KostenPage({
  onNavigate
}: KostenPageProps) {
  return <div className="w-full" data-id="element-557">
      {/* Hero Section */}
      <PageHero title="Helder over kosten" subtitle="Je weet vooraf precies waar je aan toe bent. Geen verborgen kosten, geen provisies, gewoon helder advies voor een vast tarief." data-id="element-558" />

      {/* Pricing Explanation */}
      <section className="py-20 md:py-28 bg-white" data-id="element-559">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-560">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-561">
            <FadeIn className="w-full md:w-1/2" data-id="element-562">
              <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-563">
                Hoe wij werken
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-564">
                Ik werk niet op basis van provisie van banken of
                geldverstrekkers, maar met een vast adviestarief dat ik vooraf
                met je afspreek.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-565">
                <li className="flex items-start" data-id="element-566">
                  <CheckCircleIcon className="w-6 h-6 text-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-567" />
                  <span className="text-gray-700 font-medium" data-id="element-568">
                    Het eerste kennismakingsgesprek is altijd gratis en
                    vrijblijvend.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-569">
                  <CheckCircleIcon className="w-6 h-6 text-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-570" />
                  <span className="text-gray-700 font-medium" data-id="element-571">
                    Je ontvangt vooraf een duidelijke offerte met het vaste
                    tarief.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-572">
                  <CheckCircleIcon className="w-6 h-6 text-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-573" />
                  <span className="text-gray-700 font-medium" data-id="element-574">
                    Geen uurtje-factuurtje, maar een all-in prijs voor het
                    complete traject.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-575">
                  <CheckCircleIcon className="w-6 h-6 text-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-576" />
                  <span className="text-gray-700 font-medium" data-id="element-577">
                    Kosten voor hypotheekadvies zijn vaak fiscaal aftrekbaar.
                  </span>
                </li>
              </ul>
            </FadeIn>
            <FadeIn delay={0.2} className="w-full md:w-1/2" data-id="element-578">
              <div className="bg-navy p-10 rounded-2xl shadow-card-lg text-white" data-id="element-579">
                <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mb-6" data-id="element-580">
                  <EuroIcon className="w-7 h-7 text-gold" data-id="element-581" />
                </div>
                <h3 className="text-2xl font-bold mb-4" data-id="element-582">
                  Wat kost hypotheekadvies?
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6" data-id="element-583">
                  De exacte kosten hangen af van jouw persoonlijke situatie. Ben
                  je een starter, doorstromer of ondernemer? Gaat het om een
                  complexe scheiding of een woonboot?
                </p>
                <p className="text-gray-300 leading-relaxed mb-8" data-id="element-584">
                  Tijdens het gratis kennismakingsgesprek breng ik jouw situatie
                  in kaart en vertel ik je direct welk tarief van toepassing is.
                </p>
                <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} className="w-full" data-id="element-585">
                  Plan gratis kennismaking
                </Button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Vergelijkingskaarten */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-586">
        <div className="max-w-5xl mx-auto px-4 md:px-8" data-id="element-587">
          <SectionHeading title="Vergelijkingskaarten" subtitle="Als financieel dienstverlener zijn wij wettelijk verplicht om je vooraf te informeren over onze dienstverlening en kosten. Dit doen wij via de standaard Vergelijkingskaarten opgesteld door de AFM." data-id="element-588" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-589">
            <FadeIn delay={0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100 flex flex-col h-full" data-id="element-590">
              <div className="flex items-center gap-4 mb-6" data-id="element-591">
                <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-592">
                  <FileTextIcon className="w-6 h-6 text-navy" data-id="element-593" />
                </div>
                <h3 className="text-xl font-bold text-navy" data-id="element-594">
                  Vergelijkingskaart Hypotheek
                </h3>
              </div>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-595">
                In dit document lees je wat wij voor je kunnen betekenen op het
                gebied van hypotheken en wat de gemiddelde kosten hiervoor zijn.
              </p>
              <Button variant="secondary" href="#" icon={<DownloadIcon className="w-5 h-5" data-id="element-597" />} iconPosition="left" className="w-full" data-id="element-596">
                Download PDF
              </Button>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100 flex flex-col h-full" data-id="element-598">
              <div className="flex items-center gap-4 mb-6" data-id="element-599">
                <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-600">
                  <FileTextIcon className="w-6 h-6 text-navy" data-id="element-601" />
                </div>
                <h3 className="text-xl font-bold text-navy" data-id="element-602">
                  Vergelijkingskaart Risico's afdekken
                </h3>
              </div>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-603">
                In dit document lees je wat wij voor je kunnen betekenen op het
                gebied van levens- en schadeverzekeringen en de bijbehorende
                kosten.
              </p>
              <Button variant="secondary" href="#" icon={<DownloadIcon className="w-5 h-5" data-id="element-605" />} iconPosition="left" className="w-full" data-id="element-604">
                Download PDF
              </Button>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection title="Vragen over de kosten?" subtitle="Ik leg het je graag persoonlijk uit. Neem gerust contact op voor meer informatie." buttonText="Neem contact op" onNavigate={onNavigate} page="contact" variant="light" data-id="element-606" />
    </div>;
}